import React, {SetStateAction, useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    createTheme, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Paper,
    Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField,
    ThemeProvider,
    Tooltip, Typography
} from "@mui/material";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {
    MaterialReactTable,
    MRT_PaginationState,
    MRT_Row,
    useMaterialReactTable
} from "material-react-table";
import { getDayjsLocale, getMrtLocalization } from "../../i18n/muiAdmin";
import { t } from "../../i18n/t";
import {useGetReports} from "../../hooks/reports";
import reportsService from "../../api/reportsService";
import authService from "../../api/authService";
import information from "../../assets/information.png";
import CanvasGrid from "../CanvasGrid";
import CanvasGridDashboard from "../CanvasGridDashboard";
import axios, {AxiosError} from "axios";
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShortTextIcon from '@mui/icons-material/ShortText';
import {ErrorResponse, KeyValuePair, Reports} from '../../types/types';
import SettingsIcon from "@mui/icons-material/Settings";
import config from "../../config";
import {toast} from "react-toastify";
import EditIcon from '@mui/icons-material/Edit';
import DocumentManager from "./documents";
import { useNavigate } from 'react-router-dom';
import {
    downloadReportPdf,
    downloadReportXls,
    getErrorMessage,
} from '../../utils/reportDownloads';
const url = `${config.api.baseUrl}`
//const url = 'https://seemeworking.com:3066'
//const url = 'http://localhost:3000'


// @ts-ignore
const KeyValueManager = ({ userId, onClose, table}) => {
    const [keyValues, setKeyValues] = useState<KeyValuePair[]>([]);
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [editingItem, setEditingItem] = useState<KeyValuePair | null>(null);
    const [addingItem, setAddingItem] = useState<boolean | null>(null);
    const [isApplying, setIsApplying] = useState(false);

    useEffect(() => {
        const fetchKeyValues = async () => {
            setIsLoading(true);
            try {
                const response = await authService.getKeyValues(userId);
                //const data = await response.json();
                setKeyValues(response.data);
            } catch (error) {
                console.error('Ошибка загрузки:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchKeyValues();
    }, [userId]);

    const handleAdd = async () => {
        if (!newKey.trim()) {
            toast.warning(t('keyValue.keyEmpty'));
            return;
        }

        try {
            const result = await authService.createKeyValue({
                key: newKey,
                value: newValue,
                userId
            });

            // @ts-ignore
            setKeyValues([...keyValues, result.data]);
            setNewKey('');
            setNewValue('');
        } catch (error: unknown) {
            console.error('Ошибка добавления:', error);

            let errorMessage = t('keyValue.addFailed');

            // Проверяем, является ли ошибка AxiosError
            if (typeof error === 'object' && error !== null && 'isAxiosError' in error) {
                const axiosError = error as AxiosError<ErrorResponse>;

                if (axiosError.response?.status === 409) {
                    errorMessage = t('keyValue.duplicateKey', { key: newKey });
                } else if (axiosError.response?.data?.message) {
                    errorMessage = axiosError.response.data.message;
                } else if (axiosError.response?.data?.error) {
                    errorMessage = axiosError.response.data.error;
                } else if (axiosError.message) {
                    errorMessage = axiosError.message;
                }
            } else if (error instanceof Error) {
                // Обычная JavaScript ошибка
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                // Строковая ошибка
                errorMessage = error;
            }
            toast.error(errorMessage, {
                autoClose: 5000,
                position: "top-right"
            });
        }
    };

    const handleDelete = async (id: number) => {
        try {
            if (!window.confirm(t('keyValue.confirmDelete'))) return;
            await authService.deleteKeyValue(id);
            setKeyValues(keyValues.filter(kv => kv.id !== id));
        } catch (error) {
            // @ts-ignore
            alert(t('keyValue.errorPrefix', { message: (error as Error).message }));
        }
    };

    const handleUpdate = async (key: string, newValue: string) => {
        try {
            const result = await authService.updateKeyValue({ key, value: newValue });
            // @ts-ignore
            setKeyValues(keyValues.map(kv => kv.id === result.data.id ? result.data : kv));
        } catch (error) {
            // @ts-ignore
            alert(t('keyValue.errorPrefix', { message: (error as Error).message }));
        }
    };

    // @ts-ignore
    return (
        <Box>
            {isLoading ? (
                <CircularProgress />
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('keyValue.key')}</TableCell>
                                    <TableCell>{t('keyValue.value')}</TableCell>
                                    <TableCell>{t('keyValue.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {keyValues.length > 0 ? (
                                    keyValues.map((kv) => (
                                        <TableRow key={kv.id}>
                                            <TableCell>{kv.key}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    value={kv.value}
                                                    //onChange={(e) => handleUpdate(kv.key, e.target.value)}
                                                    size="small"
                                                    disabled={true}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => handleDelete(kv.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                                <Tooltip title={t('common.edit')}>
                                                    <IconButton onClick={() => setEditingItem(kv)} color="primary">
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            {isLoading ? t('common.loading') : t('common.noData')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {editingItem && (
                        <Dialog open fullWidth maxWidth="md">
                            <DialogTitle>{t('keyValue.editTitle', { key: editingItem.key })}</DialogTitle>
                            <DialogContent>
                                <TextField
                                    multiline
                                    fullWidth
                                    minRows={20}
                                    maxRows={30}
                                    value={editingItem.value}
                                    onChange={(e) => setEditingItem({...editingItem, value: e.target.value})}
                                    variant="outlined"
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setEditingItem(null)}>{t('common.cancel')}</Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={async () => {
                                        await handleUpdate(editingItem.key, editingItem.value);
                                        setEditingItem(null);
                                    }}
                                >
                                    {t('common.save')}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}
                    <Box mt={2} display="flex" gap={1}>

                        <Button
                            variant="contained"
                            onClick={()=>setAddingItem(true)}
                        >
                            {t('common.add')}
                        </Button>

                    </Box>

                    {addingItem && (
                        <Dialog open fullWidth maxWidth="md">
                            <DialogTitle>{t('keyValue.addTitle')}</DialogTitle>

                            <DialogContent>
                                <Box mt={2} display="flex" flexDirection="column" gap={1}>
                                    <TextField
                                        label={t('keyValue.newKey')}
                                        value={newKey}
                                        onChange={(e) => setNewKey(e.target.value)}
                                        size="small"
                                    />

                                    <TextField
                                        multiline
                                        fullWidth
                                        minRows={20}
                                        maxRows={30}
                                        value={newValue}
                                        onChange={(e) => setNewValue(e.target.value)}
                                        variant="outlined"
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setAddingItem(false)}>{t('common.cancel')}</Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={async () => {
                                        await handleAdd();
                                        setAddingItem(false)
                                    }}
                                >
                                    {t('common.save')}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}
                </>
            )}
        </Box>
    );
};

const ReportsComponent = () => {
    const navigate = useNavigate();
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const {
        data: reportsPage,
        isError: isLoadingUsersError,
        isFetching: isFetchingUsers,
        isLoading: isLoadingUsers,
        refetch
    } = useGetReports(undefined, pagination.pageIndex, pagination.pageSize);
    const fetchedReports = reportsPage?.items ?? [];
    const reportsTotal = reportsPage?.total ?? 0;
    const [loading, setLoading] = useState(false);
    const [rowFilters, setRowFilters] = useState<Record<number, boolean>>({});
    const prevRowFiltersRef = useRef();
    const [user, setUser] = useState<{ id?: number; first_name?: string; last_name?: string; middle_name?: string; age?: number; weight?: number; height?: number; gender?: number }>({ id: undefined });
    useEffect(() => {
        authService.get().then((res) => setUser(res?.data ?? { id: undefined }));
    }, []);
    //DELETE action
    const openDeleteConfirmModal = (row: MRT_Row<Reports>) => {
        if (window.confirm('Are you sure you want to delete this ecg')) {
            const payload = {
                value: row.original.value,
                pin: row.original.pin
            };
            reportsService.delete(payload).then(()=>{
                refetch();
            });
        }
    };
    const [loadingRowsXls, setLoadingRowsXls] = useState<Record<number, boolean>>({});
    const [loadingRowsPdf, setLoadingRowsPdf] = useState<Record<number, boolean>>({});
    const [loadingRowsShortPdf, setLoadingRowsShortPdf] = useState<Record<number, boolean>>({});
    const handleRowXls = async (report: Reports) => {
        setLoadingRowsXls((prev) => ({ ...prev, [report.id]: true }));
        try {
            await downloadReportXls(report, user, !rowFilters[report.id]);
        } catch (err) {
            toast.error(getErrorMessage(err, t('reportDetail.xlsFailed')));
        } finally {
            setLoadingRowsXls((prev) => ({ ...prev, [report.id]: false }));
        }
    };
    const handleRowPdf = async (report: Reports, short: boolean) => {
        if (!user?.id) return;
        const setLoading = short ? setLoadingRowsShortPdf : setLoadingRowsPdf;
        setLoading((prev) => ({ ...prev, [report.id]: true }));
        try {
            await downloadReportPdf(report, user.id, short);
        } catch (err) {
            toast.error(getErrorMessage(err, short ? t('reportDetail.shortPdfFailed') : t('reportDetail.pdfFailed')));
        } finally {
            setLoading((prev) => ({ ...prev, [report.id]: false }));
        }
    };
    useEffect(()=>{

        const prevRowFilters = prevRowFiltersRef.current;

        if (prevRowFilters) {
            // Сравниваем предыдущее и текущее значение
            // @ts-ignore
            const changedKeys = [];

            // Проверяем все ключи из предыдущего объекта
            Object.keys(prevRowFilters).forEach(key => {
                // @ts-ignore
                if (prevRowFilters[key] !== rowFilters[key]) {
                    changedKeys.push(key);
                }
            });

            // Проверяем новые ключи, которых не было в предыдущем объекте
            Object.keys(rowFilters).forEach(key => {
                if (!(key in prevRowFilters)) {
                    changedKeys.push(key);
                }
            });

            if (changedKeys.length > 0) {

                // @ts-ignore
                const report = fetchedReports.find((row) => row.id ===Number(changedKeys[0]))
                if(report) {
                    handlePoints(report.value, report.pin, report.id)
                }
            }
        }

        // Сохраняем текущее значение для следующего рендера
        // @ts-ignore
        prevRowFiltersRef.current = rowFilters;
        //handlePoints(row.original.value, row.original.pin, row.original.id)

    },[rowFilters])
    const columns = useMemo(
        () => {
            // @ts-ignore

            // @ts-ignore
            return [
                {
                    accessorKey: 'id',
                    header: 'Id',
                    enableEditing: false,
                    size: 50,
                    // @ts-ignore
                    Cell: ({cell}) => (
                        <div
                            onClick={() => {
                                handlePoints(cell.row.original.value, cell.row.original.pin, cell.row.original.id)
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {cell.getValue()}
                        </div>
                    ),
                },
                {
                    accessorKey: 'time',
                    header: 'Time',
                    enableEditing: false,
                    size: 50,
                    // @ts-ignore
                    Cell: ({cell, row}) => (
                        <div
                            onClick={() => {
                                handlePoints(cell.row.original.value, cell.row.original.pin, cell.row.original.id)
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {cell.getValue()}
                        </div>
                    ),
                },
                {
                    accessorKey: 'value',
                    header: 'value',
                    enableEditing: false,
                    size: 50,
                    // @ts-ignore
                    Cell: ({cell}) => (
                        <div
                            onClick={() => handlePoints(cell.row.original.value, cell.row.original.pin, cell.row.original.id)}
                            style={{ cursor: 'pointer' }}
                        >
                            {cell.getValue()}
                        </div>
                    ),
                },{
                    accessorKey: 'pin',
                    header: 'pin',
                    enableEditing: false,
                    size: 50,
                    // @ts-ignore
                    Cell: ({cell}) => (
                        <div
                            onClick={() => handlePoints(cell.row.original.value, cell.row.original.pin, cell.row.original.id)}
                            style={{ cursor: 'pointer' }}
                        >
                            {cell.getValue()}
                        </div>
                    ),
                },{
                    accessorKey: 'created_at',
                    header: 'created_at',
                    enableEditing: false,
                    size: 50,
                    // @ts-ignore
                    Cell: ({cell}) => {
                        const formatDate = (dateString:any) => {
                            const date = new Date(dateString);
                            return date.toLocaleString(navigator.language, {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false,
                            });
                        };
                        return (
                            <div
                                onClick={() => handlePoints(cell.row.original.value, cell.row.original.pin)}
                                style={{ cursor: 'pointer' }}
                            >
                                {formatDate(cell.getValue())}
                            </div>
                        )
                    },
                },
                {
                    id: 'testConfiguration',
                    header: t('reports.testConfig'),
                    enableEditing: false,
                    size: 120,
                    Cell: ({ row }: { row: MRT_Row<Reports> }) => {
                        const r = row.original;
                        const label =
                            r.testConfiguration?.name?.trim() ||
                            (r.testConfigurationId != null ? `#${r.testConfigurationId}` : '—');
                        return (
                            <div
                                onClick={() => handlePoints(r.value, r.pin, r.id)}
                                style={{ cursor: 'pointer' }}
                            >
                                {label}
                            </div>
                        );
                    },
                },
                {
                    accessorKey: 'comment',
                    header: 'comment',
                    size: 50,
                    // @ts-ignore
                    enableEditing: true,
                    // @ts-ignore
                    muiEditTextFieldProps: ({ cell, row, table }) => ({
                        // @ts-ignore
                        onBlur: async (event) => {
                            const payload = {
                                value: row.getValue('value'),
                                pin: row.getValue('pin'),
                                comment: event.target.value
                            };
                            const response = await reportsService.postComment(payload)
                             table.setEditingCell(null)
                        },
                    }),
                    // @ts-ignore
                    Cell: ({ cell }) => <span>{cell.getValue<string>()}</span>,
                },
                {
                    id: 'filterToggle',
                    header: 'Filter',
                    size: 50,
                    enableEditing: false,
                    // @ts-ignore
                    Cell: ({ row }) => (
                        <Switch
                            checked={!rowFilters[row.original.id] || false}
                            onChange={() => {
                                setRowFilters(prev => ({
                                    ...prev,
                                    [row.original.id]: !prev[row.original.id]
                                }));
                            }}
                        />
                    )

                },

            ];
        },
        [fetchedReports, rowFilters, t],
    );





    // @ts-ignore

    // @ts-ignore
    const table = useMaterialReactTable({
        columns,
        enableEditing: true,
        editDisplayMode: 'cell',
        localization: getMrtLocalization(),
        enableSelectAll:false,
        data:fetchedReports,
        enableColumnFilterModes: false,
        enableColumnOrdering: true,
        enableColumnPinning: true,
        enableFacetedValues: true,
        enableRowActions: true,
        enableRowSelection: true,
        initialState: {
            showColumnFilters: true,
            columnPinning: {
                right: ['mrt-row-actions'],
            },
        },
        paginationDisplayMode: 'pages',
        positionToolbarAlertBanner: 'bottom',
        muiSearchTextFieldProps: {
            size: 'small',
            variant: 'outlined',
        },
        muiPaginationProps: {
            color: 'secondary',
            rowsPerPageOptions: [10, 20, 30, 50, 100],
            shape: 'rounded',
            variant: 'outlined',

        },
        muiTableBodyRowProps: ({ row }) => ({
            onClick: () => {
                if (user?.id) navigate(`/dashboard/users/${user.id}/reports/${row.original.id}`);
            },
            sx: { cursor: user?.id ? 'pointer' : 'default' },
        }),
        muiBottomToolbarProps:{
            sx: {
                justifyContent: 'flex-start',
            }
        },
        enablePagination: true,
        manualPagination: true,
        rowCount: reportsTotal,
        onPaginationChange: setPagination,

        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', gap: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                <Tooltip title="XLS">
                    <IconButton
                        size="small"
                        onClick={() => handleRowXls(row.original)}
                        disabled={!!loadingRowsXls[row.original.id]}
                    >
                        {loadingRowsXls[row.original.id] ? <CircularProgress size={20} /> : <DescriptionIcon />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="PDF">
                    <IconButton
                        size="small"
                        onClick={() => handleRowPdf(row.original, false)}
                        disabled={!!loadingRowsPdf[row.original.id]}
                    >
                        {loadingRowsPdf[row.original.id] ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('reportDetail.shortPdf')}>
                    <IconButton
                        size="small"
                        onClick={() => handleRowPdf(row.original, true)}
                        disabled={!!loadingRowsShortPdf[row.original.id]}
                    >
                        {loadingRowsShortPdf[row.original.id] ? <CircularProgress size={20} /> : <ShortTextIcon />}
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('reports.deleteRow')}>
                    <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
        renderBottomToolbarCustomActions:({table})=>(
            <Box></Box>
        ),
        state: {
            isLoading: isLoadingUsers,
            pagination,
        },

    });
    type ChartPoint = { x: number; y: number; y1?: number; isRPeaks: boolean; accelX?: number; accelY?: number; accelZ?: number; marker?: unknown; sdnn?: unknown; rmssd?: unknown; pnn20?: unknown; pnn50?: unknown };
    const [allPoints, setAllPoints] = useState<ChartPoint[]>([{ x: 0, y: 0, y1: 0, isRPeaks: false }]);

    const scale = (points: ChartPoint[]): ChartPoint[] => {
        if (!Array.isArray(points) || points.length === 0) return [];
        const minValValue = Math.max(0, Math.min(...points.map((obj) => obj.y)));
        const maxValValue = Math.min(300, Math.max(...points.map((obj) => obj.y)));
        const nonZeroPhotos = points.filter((obj) => obj.y1 !== 0).map((obj) => obj.y1 as number);
        const minValPhoto = nonZeroPhotos.length ? Math.min(...nonZeroPhotos) : 0;
        const maxValPhoto = nonZeroPhotos.length ? Math.max(...nonZeroPhotos) : 0;
        const nonZeroAccelX = points.filter((obj) => obj.accelX !== 0).map((obj) => obj.accelX as number);
        const minValAccelX = nonZeroAccelX.length ? Math.min(...nonZeroAccelX) : 0;
        const maxValAccelX = nonZeroAccelX.length ? Math.max(...nonZeroAccelX) : 0;
        const nonZeroAccelY = points.filter((obj) => obj.accelY !== 0).map((obj) => obj.accelY as number);
        const minValAccelY = nonZeroAccelY.length ? Math.min(...nonZeroAccelY) : 0;
        const maxValAccelY = nonZeroAccelY.length ? Math.max(...nonZeroAccelY) : 0;
        const nonZeroAccelZ = points.filter((obj) => obj.accelZ !== 0).map((obj) => obj.accelZ as number);
        const minValAccelZ = nonZeroAccelZ.length ? Math.min(...nonZeroAccelZ) : 0;
        const maxValAccelZ = nonZeroAccelZ.length ? Math.max(...nonZeroAccelZ) : 0;
        return points.map((obj) => ({
            ...obj,
            y: obj.y,
            y1: obj.y1 === 0 || obj.y1 == null ? obj.y1 : (maxValPhoto === minValPhoto ? maxValValue : ((obj.y1 - minValPhoto) / (maxValPhoto - minValPhoto)) * (maxValValue - minValValue)),
            accelX: obj.accelX === 0 || obj.accelX == null ? obj.accelX : (maxValAccelX === minValAccelX ? maxValValue : ((obj.accelX - minValAccelX) / (maxValAccelX - minValAccelX)) * (maxValValue - minValValue)),
            accelY: obj.accelY === 0 || obj.accelY == null ? obj.accelY : (maxValAccelY === minValAccelY ? maxValValue : ((obj.accelY - minValAccelY) / (maxValAccelY - minValAccelY)) * (maxValValue - minValValue)),
            accelZ: obj.accelZ === 0 || obj.accelZ == null ? obj.accelZ : (maxValAccelZ === minValAccelZ ? maxValValue : ((obj.accelZ - minValAccelZ) / (maxValAccelZ - minValAccelZ)) * (maxValValue - minValValue)),
        }));
    };
    const handlePoints = (value='62e4ee44350abe53a55b07f7a552d3d9', pinParam='62e4ee4435', id=0)=>{
        if (value) {

            //axios.defaults.headers.post['Access-Control-Allow-Origin'] = '*';

            const data = pinParam?{value, pin: pinParam||pinParam||'', withFilter:rowFilters[id]||false}:{value, withFilter:rowFilters[id]||false}
            axios.post(`${url}/reports/get`, data)
                .then((response: { data: any }) => {
                    const rawPoints = response.data?.points;
                    const points: ChartPoint[] = Array.isArray(rawPoints)
                        ? rawPoints.map((el: any, idx: number) => {
                            const photo = el.photo;
                            const accel = el.accel;
                            if (!accel && !el.accelX) {
                                return { x: idx, y: el.value, y1: photo, isRPeaks: false, marker: el.marker, sdnn: el.sdnn, rmssd: el.rmssd, pnn20: el.pnn20, pnn50: el.pnn50 };
                            }
                            return { x: idx, y: el.value, y1: photo, accelX: el.accelX || accel?.[0] || 0, accelY: el.accelY || accel?.[1] || 0, accelZ: el.accelZ || accel?.[2] || 0, isRPeaks: false, marker: el.marker, sdnn: el.sdnn, rmssd: el.rmssd, pnn20: el.pnn20, pnn50: el.pnn50 };
                        })
                        : [];
                    setAllPoints(points.length > 0 ? scale(points) : [{ x: 0, y: 0, y1: 0, isRPeaks: false }]);

                    setLoading(false);


                })
                .catch((error: { message: SetStateAction<null>; }) => {
                    // setError(error.message);
                    setLoading(false);
                });



        } else {
            // @ts-ignore
            //setError('Token not provided');
            //setLoading(false);
        }
    }
    // @ts-ignore
    const [selectedUser, setSelectedUser] = useState<UserPair>(null);
    const [keyValueModalOpen, setKeyValueModalOpen] = useState(false);
    const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
    return (
        <>

            <div className="user">
                <div className="user-data">
                    <div>
                        <img src={information}  />
                    </div>
                    {user && user.id &&(
                        <>
                            <div>
                                <p className="label">
                                    Id:
                                </p>
                                <p>
                                    {user?.id}
                                </p>
                            </div>
                            <div>
                                <p className="label">
                                    Name:
                                </p>
                                <p>
                                    {[user?.last_name, user?.first_name, user?.middle_name].filter(Boolean).join(' ')}
                                </p>
                            </div>
                            <div>
                                <p className="label">
                                    Age:
                                </p>
                                <p>
                                    {user?.age}
                                </p>
                            </div>
                            <div>
                                <p className="label">
                                    Weight:
                                </p>
                                <p>
                                    {user?.weight}
                                </p>
                            </div>
                            <div>
                                <p className="label">
                                    Height:
                                </p>
                                <p>
                                    {user?.height}
                                </p>
                            </div>
                            <div>
                                <p className="label">
                                    Gender:
                                </p>
                                <p>
                                    {user?.gender===1?'Male':'Female'}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
            {allPoints.length>1 && (
                <CanvasGridDashboard points={allPoints} setPulsUser={()=>{}}/>
            )}
            <Box sx={{ display: 'flex', gap: '1rem', marginTop:'1rem', marginBottom:'1rem'  }}>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={()=>{
                        setSelectedUser({id:user.id||1});
                        setKeyValueModalOpen(true);
                    }}
                >
                    {t('keyValue.userProps')}
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={()=>{
                        setSelectedUser({id:user.id||1});
                        setDocumentsModalOpen(true);
                    }}
                >
                    {t('keyValue.userFiles')}
                </Button>
            </Box>
            <Dialog
                open={keyValueModalOpen}
                onClose={() => setKeyValueModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t('keyValue.userProps')}
                </DialogTitle>
                <DialogContent>
                    {selectedUser && (
                        <KeyValueManager
                            userId={selectedUser.id}
                            onClose={() => setKeyValueModalOpen(false)}
                            table={table}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setKeyValueModalOpen(false)}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>


            <Dialog
                open={documentsModalOpen}
                onClose={() => setDocumentsModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t('keyValue.userFiles')}
                </DialogTitle>
                <DialogContent>
                    {selectedUser && (
                        <DocumentManager
                            userId={selectedUser.id}
                            onClose={() => setKeyValueModalOpen(false)}
                            table={table}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDocumentsModalOpen(false)}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            <MaterialReactTable
            table={table} />


        </>
    )
};

const DashboardReports = () => {


    const theme = createTheme();

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getDayjsLocale()}>
            <ThemeProvider theme={theme}>

                <Box
                    sx={{
                        width:'100%'
                    }}
                >

                    <ReportsComponent />
                </Box>
            </ThemeProvider>
        </LocalizationProvider>

    );
};

export default DashboardReports;
